import React from "react";
import DataTable from "../DataTable";
import { useAuthz } from "../../auth/useAuthz";
import { ArrowLeft } from "lucide-react";
import { DECLINE_CATEGORY_LABELS, DECLINE_REASON_LABELS } from "./declineOptions";

const STATUS_COLORS = {
  Completed: "bg-green-100 text-green-700",
  Pending: "bg-amber-100 text-amber-700",
  Confirmed: "bg-emerald-100 text-emerald-700",
  Cancelled: "bg-red-100 text-red-700",
  Declined: "bg-rose-100 text-rose-700",
  Started: "bg-violet-100 text-violet-700",
  Headed: "bg-indigo-100 text-indigo-700",
  Reached: "bg-sky-100 text-sky-700",
  Scanned: "bg-teal-100 text-teal-700",
};

const DashboardBookingTable = ({
  data = [],
  loading,
  onBack,
  openEditForm,
  onDelete,
  showReasonColumn = false,
  showVerifiedAtColumn = false,
}) => {
  const { can } = useAuthz();
  const safeData = Array.isArray(data) ? data : [];

  const formatVerifiedAt = (val) => {
    if (!val) return "—";
    const num = Number(val);
    const d = new Date(isNaN(num) ? val : num);
    return isNaN(d.getTime()) ? "—" : d.toLocaleString();
  };

  const formatDeclineCategory = (val) => DECLINE_CATEGORY_LABELS[val] || val || "—";
  const formatDeclineReason = (val) => DECLINE_REASON_LABELS[val] || val || "—";

  return (
    <div className="w-full p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white text-black shadow hover:bg-gray-100 transition"
          >
            <ArrowLeft size={16} />
            Back to Dashboard
          </button>
        </div>
      </div>

      {/* Table Card */}
      <div className="bg-[#111111] rounded-2xl shadow-xl overflow-hidden border border-[#1f1f1f]">
        {loading ? (
          <div className="flex justify-center py-10 text-[#FBB033]">
            <span className="animate-spin rounded-full h-6 w-6 border-t border-b border-[#FBB033]" />
          </div>
        ) : safeData.length === 0 ? (
          <div className="text-center py-10 text-gray-400">
            No bookings found for selected filter.
          </div>
        ) : (
          <div className="hidden md:block overflow-x-auto">
            <DataTable tableName="DashboardBookingTable">
              <thead className="bg-[#1a1a1a]">
                <tr>
                  {(showReasonColumn
                    ? [
                        "Booking ID",
                        "Customer",
                        "Employee",
                        "Date",
                        "Amount",
                        "Category",
                        "Sub-category",
                        "Reason",
                        showVerifiedAtColumn ? "Verified At" : null,
                        "Actions",
                      ]
                    : [
                        "Booking ID",
                        "Customer",
                        "Employee",
                        "Date",
                        "Amount",
                        "Status",
                        showVerifiedAtColumn ? "Verified At" : null,
                        "Actions",
                      ]
                  ).filter(Boolean).map((col) => (
                    <th
                      key={col}
                      className="px-4 py-3 text-white text-sm font-semibold"
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {safeData.map((booking) => (
                  <tr
                    key={booking.id}
                    className="border-b border-[#1f1f1f] hover:bg-[#1c1c1c] transition"
                  >
                    <td className="px-4 py-3 text-white">
                      {booking.bookingId}
                    </td>
                    <td className="px-4 py-3 text-white">
                      {booking.user?.fullName}
                    </td>
                    <td className="px-4 py-3 text-white">
                      {booking.employee?.fullName || "—"}
                    </td>
                    <td className="px-4 py-3 text-white">
                      {booking.bookingDate}, {booking.timeSlot}
                    </td>
                    <td className="px-4 py-3 text-white">₹ {booking.price}</td>
                    {showReasonColumn ? (
                      <>
                        <td className="px-4 py-3 text-white">
                          {formatDeclineCategory(booking.declineCategory)}
                          {booking.cancelledByType === "EMPLOYEE" && (
                            <span className="ml-1 text-amber-400 text-xs">
                              (by professional)
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-white">
                          {formatDeclineReason(booking.declineReason)}
                        </td>
                        <td className="px-4 py-3 text-white">
                          {booking.reason || "—"}
                        </td>
                      </>
                    ) : (
                      <td className="px-4 py-3">
                        <span
                          className={`px-3 py-1 text-xs font-semibold rounded-full ${
                            STATUS_COLORS[booking.status] ||
                            "bg-gray-200 text-gray-700"
                          }`}
                        >
                          {booking.status}
                        </span>
                      </td>
                    )}
                    {showVerifiedAtColumn && (
                      <td className="px-4 py-3 text-white text-xs">
                        {formatVerifiedAt(booking.verifiedAt)}
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        {can("bookings.edit") && (
                          <button
                            onClick={() => openEditForm(booking)}
                            className="bg-white text-black px-3 py-1 rounded-md text-sm hover:bg-gray-200"
                          >
                            Edit
                          </button>
                        )}

                        {can("bookings.delete") && (
                          <button
                            onClick={() => onDelete?.(booking)}
                            className="bg-red-600 text-white px-3 py-1 rounded-md text-sm hover:bg-red-700"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
          </div>
        )}

        {/* Mobile: card view */}
        {!loading && safeData.length > 0 && (
          <div className="md:hidden flex flex-col gap-3 p-4">
            {safeData.map((booking) => (
              <div
                key={booking.id}
                className="relative bg-[#0c0b0b] border border-[#2a2a2a] rounded-xl p-4 shadow"
              >
                <div className="flex items-start justify-between gap-2">
                  <span className="text-white font-semibold text-sm">
                    {booking.bookingId}
                  </span>
                  {!showReasonColumn && (
                    <span
                      className={`px-2 py-0.5 text-[10px] font-semibold rounded-full ${
                        STATUS_COLORS[booking.status] ||
                        "bg-gray-200 text-gray-700"
                      }`}
                    >
                      {booking.status}
                    </span>
                  )}
                </div>

                <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-sm">
                  <div>
                    <div className="text-gray-400 text-[11px] uppercase tracking-wide">
                      Customer
                    </div>
                    <div className="text-white break-words">
                      {booking.user?.fullName || "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-400 text-[11px] uppercase tracking-wide">
                      Employee
                    </div>
                    <div className="text-white">
                      {booking.employee?.fullName || "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-400 text-[11px] uppercase tracking-wide">
                      Date/Time
                    </div>
                    <div className="text-white">
                      {booking.bookingDate}, {booking.timeSlot}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-400 text-[11px] uppercase tracking-wide">
                      Amount
                    </div>
                    <div className="text-white">₹ {booking.price}</div>
                  </div>
                  {showReasonColumn && (
                    <>
                      <div>
                        <div className="text-gray-400 text-[11px] uppercase tracking-wide">
                          Category
                        </div>
                        <div className="text-white break-words">
                          {formatDeclineCategory(booking.declineCategory)}
                          {booking.cancelledByType === "EMPLOYEE" && (
                            <span className="ml-1 text-amber-400 text-xs">
                              (by professional)
                            </span>
                          )}
                        </div>
                      </div>
                      <div>
                        <div className="text-gray-400 text-[11px] uppercase tracking-wide">
                          Sub-category
                        </div>
                        <div className="text-white break-words">
                          {formatDeclineReason(booking.declineReason)}
                        </div>
                      </div>
                      <div className="col-span-2">
                        <div className="text-gray-400 text-[11px] uppercase tracking-wide">
                          Reason
                        </div>
                        <div className="text-white break-words">
                          {booking.reason || "—"}
                        </div>
                      </div>
                    </>
                  )}
                  {showVerifiedAtColumn && (
                    <div className="col-span-2">
                      <div className="text-gray-400 text-[11px] uppercase tracking-wide">
                        Verified At
                      </div>
                      <div className="text-white">
                        {formatVerifiedAt(booking.verifiedAt)}
                      </div>
                    </div>
                  )}
                </div>

                {(can("bookings.edit") || can("bookings.delete")) && (
                  <div className="flex gap-2 mt-3 pt-3 border-t border-[#2a2a2a]">
                    {can("bookings.edit") && (
                      <button
                        onClick={() => openEditForm(booking)}
                        className="flex-1 bg-white text-black px-3 py-1.5 rounded-md text-sm font-semibold hover:bg-gray-200"
                      >
                        Edit
                      </button>
                    )}

                    {can("bookings.delete") && (
                      <button
                        onClick={() => onDelete?.(booking)}
                        className="flex-1 bg-red-600 text-white px-3 py-1.5 rounded-md text-sm font-semibold hover:bg-red-700"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default React.memo(DashboardBookingTable);
